<script setup lang="ts">
  import { computed, useCssModule, useSlots } from 'vue';

  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import { IllustrationType, VignetteName } from '../Illustration/Illustration.models';
  import Illustration from '../Illustration/Illustration.vue';
  import Logo from '../Logo/Logo.vue';

  type ErrorCodes = 401 | 403 | 404 | 405 | 500 | 502 | 503 | 504;
  const errors: Record<ErrorCodes, { title: string; description: string }> = {
    401: {
      title: t('ll.httpError.401.title'),
      description: t('ll.httpError.401.description'),
    },
    403: {
      title: t('ll.httpError.403.title'),
      description: t('ll.httpError.403.description'),
    },
    404: {
      title: t('ll.httpError.404.title'),
      description: t('ll.httpError.404.description'),
    },
    405: {
      title: t('ll.httpError.405.title'),
      description: t('ll.httpError.405.description'),
    },
    500: {
      title: t('ll.httpError.50X.title'),
      description: t('ll.httpError.50X.description'),
    },
    502: {
      title: t('ll.httpError.50X.title'),
      description: t('ll.httpError.50X.description'),
    },
    503: {
      title: t('ll.httpError.50X.title'),
      description: t('ll.httpError.50X.description'),
    },
    504: {
      title: t('ll.httpError.504.title'),
      description: t('ll.httpError.504.description'),
    },
  };

  export interface HttpErrorProps {
    /**
     * A description of what possibly happened.
     */
    description?: string;

    /**
     * The error code.
     */
    errorCode: keyof typeof errors | `${keyof typeof errors}`;

    /**
     * By default, the logout button is visible unless content is provided to the `actions` slot or
     * this prop is `true`.
     */
    hideLogoutButton?: boolean;

    /**
     * A callback that will be called when the user clicks the logout button. If not provided,
     * a `logout` event will be emitted.
     */
    onLogout?: () => void;

    /**
     * A link to redirect the user to see if a hard refresh fixes the issue.
     */
    retryLink?: string;

    /**
     * The error title.
     */
    title?: string;
  }

  const props = withDefaults(defineProps<HttpErrorProps>(), {
    description: '',
    onLogout: undefined,
    hideLogoutButton: false,
    retryLink: undefined,
    title: '',
  });

  const classes = useCssModule();
  const slots = useSlots();

  const errorCodeStr = computed(() => props.errorCode.toString());
  const errorCodeInt = computed(() => parseInt(errorCodeStr.value, 10));
  const defaultInfoByErrorCode = computed(() => errors[errorCodeInt.value] || errors['500']);
  const errorTitle = computed(() => props.title || defaultInfoByErrorCode.value.title);
  const errorDescription = computed(() => props.description || defaultInfoByErrorCode.value.description);

  const showLogoutButton = computed(() => !props.hideLogoutButton && props.onLogout);
  const computedRetryLink = computed(() => props.retryLink || window.location.href);
</script>

<template>
  <div class="stash-http-error px-6 py-12" :class="classes.root" data-test="stash-http-error">
    <div :class="classes.wrapper">
      <router-link to="/" class="mb-8 block">
        <Logo color="white" width="192" />
      </router-link>

      <div class="mb-8 gap-6" :class="classes.content">
        <div class="order-2 lg:order-1" :class="classes.content__text">
          <h1 class="heading-jumbo m-0 mb-3 !text-white">
            {{ errorTitle }}
          </h1>
          <h2 class="mb-8 text-white">Error Code: {{ props.errorCode }}</h2>
          <p class="mb-4 text-white">
            {{ errorDescription }} You can <a :href="computedRetryLink" class="link">try again</a> or reach out to
            <a href="mailto:support@leaflink.com" class="link">support@leaflink.com</a> for assistance.
          </p>
        </div>
        <div class="order-1 lg:order-2" :class="classes.illustration">
          <Illustration :size="300" :type="IllustrationType.Vignette" :name="VignetteName.SearchStorefront" />
        </div>
      </div>

      <div v-if="slots.actions || showLogoutButton" class="flex items-start gap-6">
        <slot name="actions">
          <Button tertiary @click="props.onLogout">Logout</Button>
        </slot>
      </div>
    </div>
  </div>
</template>

<style module>
  @layer utilities {
    .root {
      background-color: var(--color-purple-500);
      display: flex;
      height: 100%;
      justify-content: center;
      align-items: center;
      max-width: 100vw;
    }

    .wrapper {
      max-width: 100%;
      width: 56rem;
    }

    .content {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .illustration {
      /* width and height similar to figma. */
      width: 300px;
      height: 193px;
    }

    @media screen and (width < 1024px) {
      .content {
        flex-wrap: wrap;
        gap: 0;
      }

      .illustration {
        margin-bottom: 30px;
      }
    }

    @media screen and (width >= 1024px) {
      .content__text {
        max-width: 50vw;
      }
    }

    .illustration svg {
      /*
       * We cannot use the same sizes from figma because the illustration would be smaller
       * and it would break alignment. The specified height in figma is 193px but the
       * illustration is 300px, so, (300 - 193) / 2 ~= 53, which keeps it aligned.
       */
      margin-top: -53px;
    }
  }
</style>
